import type { BaseAsset } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { LintFinding } from '../videoLinter';

import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

interface Box {
    left: number;
    right: number;
    top: number;
    bottom: number;
    width: number;
    height: number;
}

function toBox(bounds: { x: number; y: number; width: number; height: number }): Box {
    return {
        left: Math.floor(bounds.x),
        right: Math.ceil(bounds.x + bounds.width),
        top: Math.floor(bounds.y),
        bottom: Math.ceil(bounds.y + bounds.height),
        width: Math.round(bounds.width),
        height: Math.round(bounds.height),
    };
}

/**
 * The inflated "could ever occupy" box the live check actually uses. Static —
 * available whether or not the asset is currently rendered.
 */
function maxBoundsBox(asset: BaseAsset): Box {
    return toBox(asset.getFreelyPositionedParent().approximateMaxBounds);
}

/**
 * The real rendered box — where the glyphs/pixels actually land on the canvas
 * this frame. Only meaningful while the asset is being drawn; `getBounds()`
 * returns an empty/degenerate rect otherwise, so callers must treat a
 * zero-area box as "not available" rather than "no overlap".
 */
function actualBoundsBox(asset: BaseAsset): Box {
    return toBox(asset.getFreelyPositionedParent().getBounds());
}

/**
 * Applies the box-overlap rule (>= 50% of EITHER box overlapped) to a pair of
 * boxes. Returns `null` when a box has zero area (e.g. actual bounds are
 * unavailable because the asset isn't currently rendered) — the decision is
 * meaningless and the caller should fall back to another bounds source.
 */
function boxesOverlapPastThreshold(captionBox: Box, textBox: Box): boolean | null {
    const captionArea = (captionBox.right - captionBox.left) * (captionBox.bottom - captionBox.top);
    const textArea = (textBox.right - textBox.left) * (textBox.bottom - textBox.top);
    if (captionArea <= 0 || textArea <= 0) {
        return null;
    }

    const deltaX =
        Math.min(captionBox.right, textBox.right) - Math.max(captionBox.left, textBox.left);
    const deltaY =
        Math.min(captionBox.bottom, textBox.bottom) - Math.max(captionBox.top, textBox.top);
    if (deltaX < 0 || deltaY < 0) {
        return false;
    }

    const overlappedArea = deltaX * deltaY;
    return overlappedArea / captionArea >= 0.5 || overlappedArea / textArea >= 0.5;
}

interface AssetBounds {
    actual: Box;
    max: Box;
}

/**
 * Caption-vs-text overlap decision over already-resolved bounds.
 *
 * Prefers the *actual rendered* bounds. Actual bounds are only valid while an
 * asset is drawn on the canvas: if either the caption or a text asset reports a
 * zero-area box (not currently rendered), we can't trust actual bounds for that
 * pair and fall back to the inflated max bounds so we never silently drop a
 * real overlap.
 */
function captionOverlapsAnyText(caption: AssetBounds, textAssets: AssetBounds[]): boolean {
    return textAssets.some((text) => {
        const actual = boxesOverlapPastThreshold(caption.actual, text.actual);
        if (actual !== null) {
            return actual;
        }
        // Actual bounds unavailable for this pair — fall back to max bounds.
        return boxesOverlapPastThreshold(caption.max, text.max) === true;
    });
}

function resolveAssetBounds(asset: BaseAsset): AssetBounds {
    return { actual: actualBoundsBox(asset), max: maxBoundsBox(asset) };
}

function captionOverlapsTextAsset(captionAsset: BaseAsset, textAssets: BaseAsset[]): boolean {
    return captionOverlapsAnyText(
        resolveAssetBounds(captionAsset),
        textAssets.map(resolveAssetBounds),
    );
}

/**
 * Error: a scene's text asset overlaps the visible captions region. Unlike the
 * template-editor rule (which sweeps every aspect-ratio/text-preset combination
 * across two scene *designs* using inflated max bounds), this runs on the live
 * scene and compares the *actual rendered* bounds of the real caption asset
 * against the real text assets — far fewer false positives from the max-bounds
 * envelope. Falls back to max bounds only when an asset isn't currently drawn.
 */
export function checkCaptionOverlap(luminary: Luminary, _getState: GetRootState): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.sceneGroups.forEach((sceneGroup, sceneGroupIndex) => {
        if (!isVoiceDrivenSceneGroup(sceneGroup) || !sceneGroup.captions?.visible) {
            return;
        }
        const captionAsset = sceneGroup.captions.asset;
        if (!captionAsset) {
            return;
        }

        sceneGroup.scenes.forEach((scene) => {
            const textAssets = scene
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .filter((asset) => asset.isEditable && asset.text?.trim());
            if (textAssets.length === 0) {
                return;
            }

            if (captionOverlapsTextAsset(captionAsset, textAssets)) {
                findings.push({
                    check: 'caption_overlap',
                    severity: 'error',
                    sceneGroupIndex,
                    sceneId: scene.id,
                    message:
                        'Captions overlap a text asset on this scene. Move the text out of the caption region, change the text position preset, or disable captions here.',
                });
            }
        });
    });

    return findings;
}
